import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { customFetcher } from '../hooks/useFetch';

import { useGetTabletDevices } from '../hooks/reactQuerys/TabletQueries';
import { useCreateTabletSubmission } from '../hooks/reactQuerys/TabletSubmissionQueries';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { ITabletDevice } from '../Interfaces/ITabletDevice';
import { useMutation } from '@tanstack/react-query';

const ONLINE_THRESHOLD_MINUTES = 2;

function isOnline(lastSeen?: string | null): boolean {
  if (!lastSeen) return false;
  const ts = new Date(lastSeen).getTime();
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts <= ONLINE_THRESHOLD_MINUTES * 60_000;
}

interface Props {
  open: boolean;
  onClose: () => void;
  idLegalTransaction: number;
  participants: ILegalClient[];
  /** Optional: vorbelegt mit einem bestimmten Beteiligten (Direct-Mode). */
  prefillIdLegalClient?: number | null;
}

type Mode = 'direct' | 'free';

export const SendToTabletDialog: React.FC<Props> = ({
  open,
  onClose,
  idLegalTransaction,
  participants,
  prefillIdLegalClient,
}) => {
  const { tabletDevices, isLoadingTabletDevices } = useGetTabletDevices();
  const { createSubmission, isPendingCreateSubmission } =
    useCreateTabletSubmission();
  const { mutateAsync: createFree, isPending: isPendingCreateFree } =
    useMutation({
      mutationFn: (body: {
        idLegalTransaction: number;
        idTabletDevices: number[];
        Language: 'de' | 'en';
        EmailRequired: boolean;
      }) =>
        customFetcher({
          endpoint: '/tabletsubmission',
          method: 'POST',
          body: { ...body, Mode: 'free' },
        }),
    });

  const [mode, setMode] = useState<Mode>('direct');
  const [idTabletDevice, setIdTabletDevice] = useState<number | ''>('');
  const [idLegalClient, setIdLegalClient] = useState<number | ''>('');
  const [idTabletDevices, setIdTabletDevices] = useState<number[]>([]);
  const [language, setLanguage] = useState<'de' | 'en'>('de');
  const [emailRequired, setEmailRequired] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMode(prefillIdLegalClient ? 'direct' : 'direct');
      setIdLegalClient(prefillIdLegalClient ?? '');
      setIdTabletDevice('');
      setIdTabletDevices([]);
      setLanguage('de');
      setEmailRequired(true);
      setError(null);
    }
  }, [open, prefillIdLegalClient]);

  const sortedDevices = useMemo(() => {
    const arr = [...(tabletDevices ?? [])];
    arr.sort((a, b) => {
      const oA = isOnline(a.LastSeen_at) ? 0 : 1;
      const oB = isOnline(b.LastSeen_at) ? 0 : 1;
      if (oA !== oB) return oA - oB;
      return (a.Name || '').localeCompare(b.Name || '');
    });
    return arr;
  }, [tabletDevices]);

  const noTablets =
    !isLoadingTabletDevices && (sortedDevices?.length ?? 0) === 0;
  const participantCount = participants.length;

  async function handleSend() {
    setError(null);
    if (mode === 'direct') {
      if (!idTabletDevice) {
        setError('Bitte ein Tablet auswählen.');
        return;
      }
      try {
        await createSubmission({
          idTabletDevice: Number(idTabletDevice),
          idLegalTransaction,
          idLegalClient: idLegalClient ? Number(idLegalClient) : undefined,
          Language: language,
          EmailRequired: emailRequired,
        });
        onClose();
      } catch (e) {
        setError(
          'Der Auftrag konnte nicht erzeugt werden. Möglicherweise hat das Tablet bereits einen offenen Auftrag.'
        );
      }
    } else {
      // Free mode
      if (idTabletDevices.length === 0) {
        setError('Bitte mindestens ein Tablet auswählen.');
        return;
      }
      if (participantCount > 0 && idTabletDevices.length < participantCount) {
        if (
          !window.confirm(
            `Sie haben ${idTabletDevices.length} Tablet(s) für ${participantCount} Beteiligte gewählt. Trotzdem fortfahren?`
          )
        )
          return;
      }
      try {
        await createFree({
          idLegalTransaction,
          idTabletDevices,
          Language: language,
          EmailRequired: emailRequired,
        });
        onClose();
      } catch (e) {
        setError(
          'Die Aufträge konnten nicht erzeugt werden. Möglicherweise hat eines der Tablets bereits einen offenen Auftrag.'
        );
      }
    }
  }

  function toggleTablet(id: number) {
    setIdTabletDevices(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  const isMutating = isPendingCreateSubmission || isPendingCreateFree;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>An Tablet senden</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {noTablets && (
            <Alert severity='info'>
              Es ist noch kein Tablet gekoppelt. Bitte zuerst unter
              „Einstellungen → Geräte → Tablets" ein Tablet einrichten.
            </Alert>
          )}

          <FormControl>
            <RadioGroup
              row
              value={mode}
              onChange={e => setMode(e.target.value as Mode)}
            >
              <FormControlLabel
                value='direct'
                control={<Radio />}
                label='Direkt zuordnen'
              />
              <FormControlLabel
                value='free'
                control={<Radio />}
                label='Freier Modus'
              />
            </RadioGroup>
            <FormHelperText>
              {mode === 'direct'
                ? 'Ein Tablet wird einem bestimmten Beteiligten zugeordnet.'
                : 'Mehrere Tablets — Mandanten wählen sich am Tablet selbst aus der Beteiligten-Liste aus.'}
            </FormHelperText>
          </FormControl>

          {mode === 'direct' && (
            <>
              <FormControl fullWidth disabled={noTablets}>
                <InputLabel id='tablet-select-label'>Tablet</InputLabel>
                <Select
                  labelId='tablet-select-label'
                  label='Tablet'
                  value={idTabletDevice}
                  onChange={e => setIdTabletDevice(Number(e.target.value))}
                >
                  {sortedDevices.map((d: ITabletDevice) => (
                    <MenuItem key={d.idTabletDevice} value={d.idTabletDevice}>
                      <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ width: '100%' }}
                      >
                        <Box sx={{ flex: 1 }}>{d.Name}</Box>
                        {isOnline(d.LastSeen_at) ? (
                          <Chip label='Online' color='success' size='small' />
                        ) : (
                          <Chip label='Offline' size='small' />
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id='participant-select-label'>
                  Beteiligten zuordnen (optional)
                </InputLabel>
                <Select
                  labelId='participant-select-label'
                  label='Beteiligten zuordnen (optional)'
                  value={idLegalClient}
                  onChange={e =>
                    setIdLegalClient(
                      e.target.value ? Number(e.target.value) : ''
                    )
                  }
                >
                  <MenuItem value=''>
                    <em>– Kein Beteiligter (Mandant gibt alles neu ein) –</em>
                  </MenuItem>
                  {participants.map(p => (
                    <MenuItem key={p.idLegalClient} value={p.idLegalClient}>
                      {[p.FirstName, p.LastName].filter(Boolean).join(' ') ||
                        `Beteiligter #${p.idLegalClient}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {mode === 'free' && (
            <Box>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Tablets auswählen
                {participantCount > 0 && (
                  <Typography
                    component='span'
                    variant='caption'
                    color='text.secondary'
                    sx={{ ml: 1 }}
                  >
                    (empfohlen: mindestens {participantCount} für{' '}
                    {participantCount} Beteiligte)
                  </Typography>
                )}
              </Typography>
              <List
                dense
                sx={{
                  maxHeight: 240,
                  overflow: 'auto',
                  border: theme => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                {sortedDevices.map(d => (
                  <ListItem key={d.idTabletDevice} disablePadding>
                    <ListItemButton
                      onClick={() => toggleTablet(d.idTabletDevice)}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge='start'
                          checked={idTabletDevices.includes(d.idTabletDevice)}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={d.Name}
                        secondary={
                          isOnline(d.LastSeen_at) ? 'Online' : 'Offline'
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              {idTabletDevices.length > 0 && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 1, display: 'block' }}
                >
                  {idTabletDevices.length} Tablet(s) ausgewählt.
                </Typography>
              )}
            </Box>
          )}

          <Divider />

          <FormControl fullWidth>
            <InputLabel id='language-select-label'>Sprache</InputLabel>
            <Select
              labelId='language-select-label'
              label='Sprache'
              value={language}
              onChange={e => setLanguage(e.target.value as 'de' | 'en')}
            >
              <MenuItem value='de'>Deutsch</MenuItem>
              <MenuItem value='en'>English</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={emailRequired}
                onChange={e => setEmailRequired(e.target.checked)}
              />
            }
            label='E-Mail ist Pflichtfeld am Tablet'
          />
          {!emailRequired && (
            <Typography variant='caption' color='warning.main'>
              Der Mandant kann die Eingabe ohne E-Mail abschicken.
            </Typography>
          )}

          {error && <Alert severity='error'>{error}</Alert>}

          {mode === 'direct' && idLegalClient && (
            <Typography variant='caption' color='text.secondary'>
              Bekannte Daten dieses Beteiligten (Name, Geburt, Adresse, E-Mail,
              Telefon) werden automatisch vorbelegt — der Mandant muss sie nur
              bestätigen oder anpassen.
            </Typography>
          )}
          {mode === 'free' && (
            <Typography variant='caption' color='text.secondary'>
              Auf jedem ausgewählten Tablet erscheint zunächst eine Liste der
              Vorgangsbeteiligten. Mandanten wählen sich selbst aus oder legen
              einen neuen Eintrag an.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant='contained'
          onClick={handleSend}
          disabled={isMutating || noTablets}
        >
          {mode === 'free' ? 'Tablets aktivieren' : 'Senden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
